import { Clock, Instagram, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { CALL_LINK, EMAIL, EMAIL_LINK, MESSAGES, WA_NUMBER_DISPLAY, waLink } from "@/data/content";
import { FadeUp, SectionHead } from "./Reveal";

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

const SLOTS = [
  { icon: Mail, label: "البريد الإلكتروني", testId: "slot-email", value: EMAIL, href: EMAIL_LINK },
  { icon: MapPin, label: "تغطيتنا", testId: "slot-map", value: "نتواجد بشكل عام — افتح الخريطة", href: "https://maps.app.goo.gl/7ufxoBMvTQExLyLx8" },
  { icon: Clock, label: "أوقات العمل", testId: "slot-hours", value: "24 ساعة · طيلة أيام الأسبوع" },
];

const SOCIAL_LINKS = [
  { id: "tiktok", icon: TikTokIcon, label: "تيك توك", handle: "@Shiyadn", href: "https://www.tiktok.com/@Shiyadn" },
  { id: "instagram", icon: Instagram, label: "انستقرام", handle: "@Shiyadn0", href: "https://www.instagram.com/Shiyadn0" },
];

export default function Contact() {
  return (
    <section id="contact" data-testid="contact-section" className="grain relative bg-navy py-24 md:py-36">
      <div className="relative z-10 mx-auto max-w-7xl px-5 md:px-10">
        <SectionHead dark eyebrow="GET IN TOUCH" eyebrowAr="تواصل مع شيادن" title="قريبون منك، بخطوة واحدة" testId="contact-heading" />

        <div className="grid gap-16 lg:grid-cols-[1.4fr_1fr] lg:gap-24">
          <div>
            <FadeUp>
              <span className="font-body text-sm text-cream/50">رقم المكتب</span>
              <a
                href={CALL_LINK}
                data-testid="contact-phone-number"
                dir="ltr"
                className="mt-3 block w-fit font-display text-5xl md:text-7xl font-bold tracking-wide text-cream transition-colors duration-300 hover:text-copper"
              >
                {WA_NUMBER_DISPLAY}
              </a>
            </FadeUp>
            <FadeUp delay={0.15}>
              <div className="mt-10 flex flex-wrap gap-4">
                <a
                  href={waLink(MESSAGES.general)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-button"
                  className="group inline-flex items-center gap-3 rounded-full bg-copper px-8 py-4 font-head text-base font-bold text-navy transition-transform duration-300 hover:scale-[1.04]"
                >
                  <MessageCircle className="h-5 w-5 transition-transform duration-300 group-hover:-rotate-12" />
                  واتساب
                </a>
                <a
                  href={CALL_LINK}
                  data-testid="contact-call-button"
                  className="inline-flex items-center gap-3 rounded-full border border-cream/40 px-8 py-4 font-head text-base font-bold text-cream transition-colors duration-300 hover:border-copper hover:text-copper"
                >
                  <Phone className="h-5 w-5" />
                  اتصال مباشر
                </a>
              </div>
            </FadeUp>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {SLOTS.map((s, i) => (
              <FadeUp key={s.testId} delay={0.1 + i * 0.08} className="h-full">
                <div
                  data-testid={s.testId}
                  className="flex h-full flex-col justify-between border border-dashed border-white/20 p-5 md:p-6"
                >
                  <s.icon className={`h-6 w-6 ${s.value ? "text-copper" : "text-copper/70"}`} strokeWidth={1.5} />
                  <div className="mt-8">
                    <span className="block font-head text-sm font-bold text-cream/85">{s.label}</span>
                    {s.value ? (
                      s.href ? (
                        <a
                          href={s.href}
                          dir="ltr"
                          target={s.href?.startsWith("http") ? "_blank" : undefined}
                          rel={s.href?.startsWith("http") ? "noopener noreferrer" : undefined}
                          className="mt-1 block text-right text-xs text-copper transition-colors duration-300 hover:text-cream"
                        >
                          {s.value}
                        </a>
                      ) : (
                        <span className="mt-1 block text-xs font-semibold text-copper">{s.value}</span>
                      )
                    ) : (
                      <span className="mt-1 block text-xs text-cream/40">تُضاف قريبًا</span>
                    )}
                  </div>
                </div>
              </FadeUp>
            ))}
            <FadeUp delay={0.34} className="h-full">
              <div
                data-testid="slot-social"
                className="flex h-full flex-col justify-between border border-dashed border-white/20 p-5 md:p-6"
              >
                <span className="font-head text-sm font-bold text-cream/85">وسائل التواصل</span>
                <div className="mt-6 space-y-3">
                  {SOCIAL_LINKS.map((l) => (
                    <a
                      key={l.id}
                      href={l.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`social-link-${l.id}`}
                      className="flex items-center gap-2.5 text-xs text-copper transition-colors duration-300 hover:text-cream"
                    >
                      <l.icon className="h-4 w-4" />
                      <span>{l.label}</span>
                      <span dir="ltr">{l.handle}</span>
                    </a>
                  ))}
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
}
